import React, { useContext, useMemo } from "react";
import Select, { components } from "react-select";
import { useNavigate, useLocation } from "react-router-dom";

import { ROUTES } from "constants/navigation";
import { LOCALE_STRINGS } from "constants/locales";
import { LocalizationContext } from "contexts/localizationContext";
import { Icon } from "components/Icon";
import { ICONS } from "constants/icons";

const { Option, ValueContainer } = components;

const customStyle = {
  control: (base) => ({
    ...base,
    border: 'none',
    boxShadow: 'none',
    minHeight: 'auto',
    background: 'transparent',
    color: 'inherit',
    '&:hover': {
      border: 'none',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: 'inherit',
    margin: 0,
  }),
  input: (base) => ({
    ...base,
    color: 'transparent',
    caretColor: 'transparent',
    width: 0,
    margin: 0,
    padding: 0,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: 0,
    margin: 0,
  }),
};

function SolutionOption(props) {
  const { data } = props;
  const IconComponent = data.icon;

  return (
    <Option {...props}>
      {IconComponent && (
        <div className="solutionsSelect__optionIcon">
          <IconComponent />
        </div>
      )}
      <div className="solutionsSelect__optionContent">
        <span className="solutionsSelect__optionTitle">{data.label}</span>
        {data.description && (
          <span className="solutionsSelect__optionDescription">
            {data.description}
          </span>
        )}
      </div>
    </Option>
  );
}

const SolutionValueContainer = ({ children, ...props }) => {
  const { selectProps: { mainLabel } } = props;
  
  const selectInput = React.Children.toArray(children).find(
    (input) => input.type?.name === "Input" || input.type?.name === "DummyInput"
  );

  return (
    <ValueContainer inputMode="none" {...props}>
      <div className="solutionsSelect__valueContainer">
        <span>{mainLabel}</span>
        {selectInput}
      </div>
    </ValueContainer>
  );
};

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <div
        className={
          props.selectProps.menuIsOpen
            ? "solutionsSelect__arrow-container menu-open"
            : "solutionsSelect__arrow-container"
        }
      >
        <Icon icon={ICONS.selectArrow} />
      </div>
    </components.DropdownIndicator>
  );
};

export const SolutionsSelect = ({ menuPlacement = "bottom" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useContext(LocalizationContext);

  const mainTitle = useMemo(() => {
    return LOCALE_STRINGS[language]?.[ROUTES.solutions.title] || "Solutions";
  }, [language]);

  const options = useMemo(() => {
    return (ROUTES.solutions.children || []).map((child) => ({
      value: child.path,
      label: LOCALE_STRINGS[language]?.[child.title] || child.title,
      description: LOCALE_STRINGS[language]?.[child.description] || child.description || "",
      icon: child.icon,
    }));
  }, [language]);

  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === location.pathname) || null;
  }, [location.pathname, options]);

  const handleChange = (selected) => {
    if (selected?.value) {
      navigate(selected.value);
    }
  };

  return (
    <Select
      className="basic-single solutions-select"
      classNamePrefix="solutionsSelect"
      value={selectedOption}
      mainLabel={mainTitle}
      name="solutions"
      options={options}
      menuPlacement={"auto"}
      components={{
        Option: SolutionOption,
        ValueContainer: SolutionValueContainer,
        IndicatorSeparator: () => null,
        DropdownIndicator,
      }}
      onChange={handleChange}
      hideSelectedOptions={false}
      styles={customStyle}
      isSearchable={false}
    />
  );
};